'use client'

import { ReactNode, useCallback, useEffect, useState } from 'react'

import { useLocation } from '@marketplace-web/shared/browser-data'
import { useSession } from '@marketplace-web/shared/session'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { logBrazeMessage } from '@marketplace-web/braze/braze-data'

import { InitializeBrazeReturnType } from '../../unsafe-initialize'
import { requestContentCardsRefreshThrottled } from '../../utils/async-utils'
import { flushCustomEventsThrottled } from '../../utils/custom-event'
import { getCardLoggersById } from '../../utils/event-loggers'
import BrazeContext, { initialBrazeContextValue } from './BrazeContext'
import useInitializeBraze from './useInitializeBraze'
import useManageInAppMessage from './useManageInAppMessage'

type Props = {
  children: ReactNode
}

const BrazeProvider = ({ children }: Props) => {
  const [userExternalId, setUserExternalId] = useState<string | null>(null)
  const [providers, setProviders] = useState<Awaited<InitializeBrazeReturnType>['providers']>()
  const [contextValue, setContextValue] = useState(initialBrazeContextValue)
  const [isInitialized, setIsInitialized] = useState(false)
  const { relativeUrl } = useLocation()
  useManageInAppMessage(providers?.inAppMessageProvider)

  const isFetchExternalIdEnabled = useFeatureSwitch('kk_migrate_external_id_retrieval_web')
  const { user } = useSession()
  const userDtoExternalId = user?.external_id || null
  const isLoggedIn = !!user

  const externalId = isFetchExternalIdEnabled ? userExternalId : userDtoExternalId

  if (!externalId)
    // Temporary logging for monitoring new logic
    logBrazeMessage(
      `ExternalID FS: ${isFetchExternalIdEnabled ? 'ON' : 'OFF'} Attempted Braze init without externalId`,
    )

  const initializeBraze = useInitializeBraze(externalId)

  useEffect(() => {
    if (!isLoggedIn) return

    initializeBraze().then(({ initialized, stores, providers: newProviders }) => {
      const cardStores = [
        stores.inboxMessageCardStore,
        stores.inboxNotificationCardStore,
        stores.promoBoxCardStore,
      ]
      const cardLoggers = getCardLoggersById(cardStores)

      if (newProviders) setProviders(newProviders)

      setIsInitialized(initialized)
      setContextValue({
        ...stores,
        ...cardLoggers,
        userExternalId: externalId,
        setUserExternalId,
      })
    })
  }, [initializeBraze, externalId, isLoggedIn])

  const handleRelativeUrlChange = useCallback(() => {
    if (!externalId) return
    if (!isInitialized) return

    flushCustomEventsThrottled(externalId)
    requestContentCardsRefreshThrottled()
  }, [isInitialized, externalId])

  useEffect(() => {
    handleRelativeUrlChange()
  }, [relativeUrl, handleRelativeUrlChange])

  return <BrazeContext.Provider value={contextValue}>{children}</BrazeContext.Provider>
}

export default BrazeProvider
